import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { translationService, t as tFn, Locale } from '../services/translationService';

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => Promise<void>;
  t: typeof tFn;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => translationService.getLocale());

  useEffect(() => {
    translationService.initTranslations();
    setLocaleState(translationService.getLocale());
  }, []);

  const setLocale = useCallback(async (newLocale: Locale) => {
    await translationService.setLocale(newLocale);
    setLocaleState(newLocale);
  }, []);

  const value: LocaleContextValue = {
    locale,
    setLocale,
    t: tFn,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

/** Подписка на контекст локали — при смене языка компонент перерисуется и получит актуальный t() */
export function useTranslation(): typeof tFn {
  const ctx = useContext(LocaleContext);
  if (!ctx) return tFn;
  return ctx.t;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    return {
      locale: translationService.getLocale(),
      setLocale: translationService.setLocale,
      t: tFn,
    };
  }
  return ctx;
}
